/**
 * WebRTC 服务类，用于处理 WebRTC 连接和流媒体传输
 */
class WebRTCService {
  constructor() {
    // WebRTC 连接对象
    this.peerConnection = null
    // 远程媒体流
    this.remoteStream = null
  }

  /**
     * 初始化 WebRTC 连接
     * @param {Object} configuration - WebRTC 配置项，包含 ICE 服务器等信息
     * @returns {Promise<boolean>} 初始化是否成功
     */
  async initializeConnection(configuration) {
    try {
      this.peerConnection = new RTCPeerConnection(configuration)

      // 添加音视频收发器，设置为仅接收模式
      this.peerConnection.addTransceiver('video', { direction: 'recvonly' })
      this.peerConnection.addTransceiver('audio', { direction: 'recvonly' })

      this.setupPeerConnectionListeners()
      return true
    } catch (error) {
      console.error('Failed to initialize WebRTC connection:', error)
      return false
    }
  }

  /**
     * 设置 WebRTC 连接的事件监听器
     * 包括 ICE 候选者和远程流接收的处理
     */
  setupPeerConnectionListeners() {
    // 处理 ICE 候选者
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.onIceCandidate(event.candidate)
      }
    }

    // 处理接收到的远程媒体流
    this.peerConnection.ontrack = (event) => {
      this.remoteStream = event.streams[0]
      this.onRemoteStreamReceived(this.remoteStream)
    }
  }

  /**
     * 创建并设置本地 SDP offer
     * @returns {Promise<RTCSessionDescription>} 本地 SDP 描述
     */
  async createOffer() {
    try {
      const offer = await this.peerConnection.createOffer()
      await this.peerConnection.setLocalDescription(offer)
      return this.peerConnection.localDescription
    } catch (error) {
      console.error('Error creating offer:', error)
      throw error
    }
  }

  /**
     * 处理远程 SDP answer
     * @param {RTCSessionDescriptionInit} answer - 远程 SDP 答复
     */
  async handleAnswer(answer) {
    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
    } catch (error) {
      console.error('Error handling answer:', error)
      throw error
    }
  }

  // 回调函数接口
  onIceCandidate(candidate) {}
  onRemoteStreamReceived(stream) {}

  /**
     * 关闭并清理 WebRTC 连接
     */
  closeConnection() {
    if (this.peerConnection) {
      this.peerConnection.close()
    }
    this.remoteStream = null
    this.peerConnection = null
  }
}

export default WebRTCService
